import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ArrowLeft, Truck, Shield, CreditCard, Loader2, Boxes } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { cartAPI, authAPI, cartLineKey, shippingAPI } from '../services/api';
import { ga } from '../services/ga';
import CartAlternativeHint from '../components/CartAlternativeHint';
import PromoCodeField from '../components/PromoCodeField';
import SavingsSummary from '../components/SavingsSummary';
import { computeSavings, promoSession, swapStore } from '../services/savings';
import { usePromoRevalidation } from '../hooks/usePromoRevalidation';
import CartPackUpsell from '../components/CartPackUpsell';
import { useT } from '../services/i18n';
import { usePageMeta } from '../services/seo';
import { useShippingSummary, fmtEuro } from '../services/shippingInfo';
import FreeShippingProgress from '../components/FreeShippingProgress';
import CartVehicleCard from '../components/CartVehicleCard';
import { useCartFit, CartFitBadge, CartFitWarning, isCartMismatch } from '../components/CartFitBadge';
import CartCompatibleSwap from '../components/CartCompatibleSwap';
import CrossSellSection from '../components/CrossSellSection';
import { useSelectedVehicle } from '../services/vehicleStore';

const CartPage = () => {
  const { t } = useT();
  usePageMeta({ title: t('sm.cart'), noindex: true });
  const selectedVehicle = useSelectedVehicle();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [] });
  const cartFit = useCartFit(cart.items, selectedVehicle);
  const [isLoading, setIsLoading] = useState(true);
  const [promo, setPromo] = useState(() => promoSession.get());
  const isAuthenticated = authAPI.isAuthenticated();

  const loadCart = useCallback(async () => {
    try {
      const data = await cartAPI.get();
      setCart(data);
    } catch (error) {
      console.error('Error loading cart:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  const updateQuantity = async (item, delta) => {
    const step = item.pack_id ? 1 : (item.sale_multiple || 1);
    const newQuantity = item.quantity + delta * step;
    if (newQuantity <= 0) {
      await removeItem(item);
      return;
    }
    
    try {
      await cartAPI.updateItem(item.product_id, newQuantity, item.pack_id || null);
      if (delta < 0) ga.removeFromCart(item, step * -delta);
      setCart(prev => ({
        ...prev,
        items: prev.items.map(i => 
          cartLineKey(i) === cartLineKey(item) ? { ...i, quantity: newQuantity } : i
        )
      }));
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  };

  const removeItem = async (item) => {
    try {
      await cartAPI.removeItem(item.product_id, item.pack_id || null);
      ga.removeFromCart(item, item.quantity);
      if (!item.pack_id) swapStore.forget(item.product_id);
      setCart(prev => ({
        ...prev,
        items: prev.items.filter(i => cartLineKey(i) !== cartLineKey(item))
      }));
    } catch (error) {
      console.error('Error removing item:', error);
    }
  };

  const subtotal = cart.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipInfo = useShippingSummary();
  const savings = computeSavings(cart.items, promo, shipInfo);
  const [shipEstimate, setShipEstimate] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const tyreQty = cart.items.reduce((sum, item) => sum + (item.is_tyre ? item.quantity * (item.units || 1) : 0), 0);
    const weightKg = Math.round(cart.items.reduce((sum, item) => sum + (Number(item.weight_kg) || 0) * item.quantity * (item.units || 1), 0) * 100) / 100;
    shippingAPI.options(shipInfo.country || 'FR', subtotal, tyreQty, weightKg).then((d) => { if (!cancelled) setShipEstimate(d.options[0] || null); }).catch(() => {});
    return () => { cancelled = true; };
  }, [subtotal, cart.items, shipInfo.country]);
  const shipping = promo?.free_shipping ? 0 : (shipEstimate ? shipEstimate.price : savings.shipping);
  const freeFrom = shipEstimate?.free_from;
  const total = Math.max(subtotal - savings.promoDiscount + shipping, 0);
  const changePromo = (p) => { setPromo(p); promoSession.set(p); };
  usePromoRevalidation(cart.items, promo, setPromo);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/" className="flex items-center gap-2 text-blue-600 hover:underline">
            <ArrowLeft size={18} />
            {t('common.continueShopping')}
          </Link>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('cart.title', { n: cart.items.length, count: cart.items.length })}</h1>

        {cart.items.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-500 mb-4">{t('cart.empty')}</p>
            <Button onClick={() => navigate('/')} className="bg-orange-500 hover:bg-orange-600">
              {t('cart.discover')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Cart items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map(item => (
                <div key={cartLineKey(item)} data-testid={`cart-line-${item.product_id}${item.pack_id ? `-${item.pack_id}` : ''}`} className="bg-white rounded-lg shadow-sm p-4 flex flex-wrap sm:flex-nowrap gap-3 sm:gap-4">
                  <Link to={`/produit/${item.product_id}`} data-testid={`cart-item-image-${item.product_id}`} className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 block rounded hover:ring-2 hover:ring-orange-300 transition-shadow">
                    <img src={item.image} alt={item.name} className="w-full h-full object-contain" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 font-medium">{item.brand}</p>
                    <h3 className="font-medium text-gray-900 text-sm sm:text-base break-words"><Link to={`/produit/${item.product_id}`} data-testid={`cart-item-link-${item.product_id}`} className="hover:text-orange-600 hover:underline">{item.name}</Link></h3>
                    <p className="text-xs text-gray-400">{t('common.ref')} {item.ref}</p>
                    <CartFitBadge status={cartFit[item.product_id]} vehicle={selectedVehicle} productId={item.product_id} fuelTypes={cartFit.__meta?.fuel_types?.[item.product_id]} />
                    {item.pack_label && <span className="inline-flex items-center gap-1 mt-1 text-[11px] font-medium bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded-full" data-testid="cart-pack-label"><Boxes size={11} /> {item.pack_label} · {item.units} {t('cart.units')}</span>}
                    {!item.pack_id && (item.sale_multiple || 1) > 1 && <span className="inline-block mt-1 text-[11px] text-gray-500" data-testid="cart-sale-multiple">{t('cart.soldBy', { n: item.sale_multiple })}</span>}
                    <p className="text-lg font-bold text-gray-900 mt-2 flex flex-wrap items-center gap-2">
                      {item.discount_pct ? <span className="text-sm text-gray-400 line-through font-normal" data-testid="cart-base-price">{item.base_price.toFixed(2)} €</span> : null}
                      {item.price.toFixed(2)} €
                      {item.discount_pct ? <span className="text-[11px] font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full" data-testid="cart-tier-badge">−{item.discount_pct}% {item.discount_kind === 'pro' ? t('cart.proPrice') : t('cart.quantity')}</span> : null}
                    </p>
                    {isCartMismatch(cartFit[item.product_id])
                      ? <CartCompatibleSwap item={item} vehicle={selectedVehicle} onReplaced={loadCart} />
                      : <CartAlternativeHint item={item} onReplaced={loadCart} />}
                  </div>
                  <div className="w-full sm:w-auto flex sm:flex-col items-center sm:items-end justify-between gap-2 pt-3 sm:pt-0 border-t sm:border-t-0 border-gray-100">
                    <div className="flex items-center gap-1 border rounded order-first sm:order-last" data-testid={`cart-qty-${item.product_id}`}>
                      <button onClick={() => updateQuantity(item, -1)} className="p-2 hover:bg-gray-100" aria-label="-" data-testid={`cart-qty-minus-${item.product_id}`}>
                        <Minus size={16} />
                      </button>
                      <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item, 1)} className="p-2 hover:bg-gray-100" aria-label="+" data-testid={`cart-qty-plus-${item.product_id}`}>
                        <Plus size={16} />
                      </button>
                    </div>
                    <button onClick={() => removeItem(item)} className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-500 py-1" data-testid={`cart-remove-${item.product_id}`}>
                      <Trash2 size={17} /> <span className="sm:hidden">{t('cart.remove')}</span>
                    </button>
                  </div>
                </div>
              ))}
              <CrossSellSection placement="cart" productIds={cart.items.map(i => i.product_id)} cartIds={cart.items.map(i => i.product_id)} title={t('xsell.cartTitle')} subtitle={t('xsell.cartHint')} compact onAdded={loadCart} className="bg-white rounded-lg shadow-sm p-5 mt-4" testId="cart-cross-sell" />
            </div>
            {/* Order summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('cart.summary')}</h2>
                <CartVehicleCard testId="cart-vehicle" garage />
                <CartPackUpsell items={cart.items} vehicle={selectedVehicle} onAdded={loadCart} onPromo={setPromo} />
                <CartFitWarning items={cart.items} testId="cart-fit-warning" />

                {isAuthenticated ? (
                  <div className="mb-4"><PromoCodeField promo={promo} onChange={changePromo} /></div>
                ) : (
                  <p className="text-xs text-gray-500 mb-4" data-testid="cart-promo-login-hint">{t('cart.promoLogin')}</p>
                )}
                
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('common.subtotal')}</span>
                    <span className="font-medium">{subtotal.toFixed(2)} €</span>
                  </div>
                  {savings.promoDiscount > 0 && (
                    <div className="flex justify-between text-green-700" data-testid="cart-promo-line">
                      <span>{t('common.discount', { code: promo.code })}</span>
                      <span className="font-medium">-{savings.promoDiscount.toFixed(2)} €</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('common.shipping')}</span>
                    <span className="font-medium" data-testid="cart-shipping-amount">{shipping === 0 ? t('common.free') : `${shipping.toFixed(2)} €`}</span>
                  </div>
                  {shipEstimate && <p className="text-[11px] text-gray-400">{t('cart.shippingEstimate', { carrier: shipEstimate.name, country: shipInfo.countryName || 'France' })}</p>}
                  {shipEstimate?.tyre_free && <p className="text-xs text-green-600" data-testid="cart-tyre-free">{t('checkout.tyreFree')}</p>}
                  {!promo?.free_shipping && freeFrom != null && <FreeShippingProgress subtotal={subtotal} freeFrom={freeFrom} countryName={shipInfo.country !== 'FR' ? shipInfo.countryName : null} />}
                  <hr />
                  <div className="flex justify-between text-lg font-bold">
                    <span>{t('common.total')}</span>
                    <span>{total.toFixed(2)} €</span>
                  </div>
                  <p className="text-xs text-gray-500">{t('common.vatIncluded')}</p>
                  <SavingsSummary savings={savings} testId="cart-savings" />
                </div>

                <Button 
                  data-testid="cart-checkout-button"
                  onClick={() => navigate(isAuthenticated ? '/checkout' : '/connexion?redirect=/checkout')}
                  className="w-full bg-orange-500 hover:bg-orange-600 mt-6"
                >
                  {isAuthenticated ? t('cart.checkout') : t('cart.loginToOrder')}
                </Button>
                {!isAuthenticated && (
                  <p className="text-xs text-gray-500 text-center mt-2" data-testid="guest-cart-notice">
                    {t('cart.guestNotice')}
                  </p>
                )}

                {/* Trust badges */}
                <div className="mt-6 space-y-3 text-xs text-gray-500">
                  <div className="flex items-center gap-2">
                    <Truck size={16} className="text-green-600" />
                    {shipInfo.freeFrom != null ? t('cart.freeShipping120', { n: fmtEuro(shipInfo.freeFrom).replace(' €', '') }) : t('cart.fastShipping')}
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield size={16} className="text-green-600" />
                    {t('cart.return14')}
                  </div>
                  <div className="flex items-center gap-2">
                    <CreditCard size={16} className="text-green-600" />
                    {t('cart.securePayment')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default CartPage;
